import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl as resolveUrl, mediaSrcSet } from "@/lib/api";

const SIZES = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 420px";

export function getImages(item) {
  if (Array.isArray(item.images) && item.images.length > 0) return item.images;
  if (item.image) return [item.image];
  return [];
}

export default function ImageSlideshow({ images, alt, className = "", aspectClass = "aspect-[4/3]" }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const count = images.length;

  const advance = useCallback(() => setIdx(i => (i + 1) % count), [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(advance, 4000);
    return () => clearInterval(timerRef.current);
  }, [count, paused, advance]);

  if (count === 0) return <div className={`${aspectClass} bg-biga-ink/5 ${className}`} />;

  if (count === 1) {
    return (
      <div className={`${aspectClass} overflow-hidden ${className}`}>
        <img src={resolveUrl(images[0], 640)} srcSet={mediaSrcSet(images[0])} sizes={SIZES} alt={alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </div>
    );
  }

  return (
    <div
      className={`relative ${aspectClass} overflow-hidden ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="image-slideshow"
    >
      <div
        className="flex h-full transition-transform duration-500 ease-in-out"
        style={{ width: `${count * 100}%`, transform: `translateX(-${idx * (100 / count)}%)` }}
      >
        {images.map((img, i) => (
          <img key={img} src={resolveUrl(img, 640)} srcSet={mediaSrcSet(img)} sizes={SIZES} alt={`${alt} ${i + 1}`} loading="lazy" decoding="async" className="h-full object-cover shrink-0" style={{ width: `${100 / count}%` }} />
        ))}
      </div>

      {/* Arrows */}
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx((idx - 1 + count) % count); }}
        className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60"
        aria-label="Previous image"
      >
        <ChevronLeft size={16} />
      </button>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx((idx + 1) % count); }}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60"
        aria-label="Next image"
      >
        <ChevronRight size={16} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((img, i) => (
          <button
            key={img?.id || `dot-${i}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
            className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-3" : "bg-white/50"}`}
            aria-label={`Image ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
